/**
 * Shared `useDayTypes` — the single canonical React Query hook for the
 * user's resolved day-type palette.
 *
 * Why this exists:
 *   Dashboard, BreakfastModal, Settings, and (transitively) Analytics all
 *   consumed the same underlying tables (`system_day_types` + overrides)
 *   via slightly different hand-rolled queries. Three of those bugs:
 *     • BreakfastModal read `ov.color` instead of the canonical
 *       `ov.custom_color` — users saw stale colors after renaming.
 *     • Each copy picked its own `staleTime` (5 min / Infinity / default)
 *       which made cache invalidation non-deterministic.
 *     • Cache keys were string literals (`["dayTypes", userId]`) so a typo
 *       in any one copy silently split the cache.
 *
 * Canonical shape matches `DayType` from `@/contexts/AppContext` so the hook
 * can slot into any existing consumer without a translation layer.
 */

import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DayType } from "@/contexts/AppContext";

// ─── Query keys (centralized — NEVER use the string literal directly) ──────
export const DAY_TYPES_ROOT = "dayTypes" as const;

/** Prefix key — pass to `invalidateQueries` / `removeQueries` for fan-out. */
export const dayTypesPrefix = [DAY_TYPES_ROOT] as const;

/** Full query key for a specific user. */
export function dayTypesKey(userId: string | null | undefined) {
  return [DAY_TYPES_ROOT, userId ?? null] as const;
}

// ─── Supabase row shapes ───────────────────────────────────────────────────
interface SystemDayTypeRow {
  id: string;
  system_name: string;
  system_color: string | null;
  is_user_selectable: boolean;
  base_category: string;
}

interface OverrideRow {
  system_day_type_id: string;
  custom_name: string | null;
  custom_color: string | null;
}

export async function fetchDayTypes(userId: string): Promise<DayType[]> {
  const [sysRes, overRes] = await Promise.all([
    supabase.from("system_day_types").select("*"),
    supabase.from("user_day_type_overrides").select("*").eq("user_id", userId),
  ]);

  if (sysRes.error) throw sysRes.error;

  const systemRows = (sysRes.data ?? []) as SystemDayTypeRow[];
  const overrideRows = (overRes.data ?? []) as OverrideRow[];
  const overrides = new Map(overrideRows.map((o) => [o.system_day_type_id, o]));

  return systemRows.map((st) => {
    const ov = overrides.get(st.id);
    return {
      id:           st.id,
      name:         ov?.custom_name  || st.system_name,
      color:        ov?.custom_color || st.system_color || "",
      visible:      st.is_user_selectable,
      baseCategory: st.base_category,
    };
  });
}

// ─── Hook ──────────────────────────────────────────────────────────────────

/** Options that consumers might reasonably want to override per call site. */
export type UseDayTypesOptions = Pick<
  UseQueryOptions<DayType[]>,
  "enabled" | "staleTime" | "gcTime" | "refetchOnWindowFocus" | "throwOnError"
>;

/**
 * Resolves the user's visible day-type palette (system types + personal
 * overrides).
 *
 * Defaults are optimized for the reality of this data: day-types are edited
 * from the Settings page (and rarely), so we can safely cache for 5 minutes
 * and ignore window-focus refetches. Any consumer that explicitly needs
 * different behavior can override.
 */
export function useDayTypes(
  userId: string | null | undefined,
  options: UseDayTypesOptions = {},
): UseQueryResult<DayType[]> {
  return useQuery<DayType[]>({
    queryKey: dayTypesKey(userId),
    // `!!userId` already gates fetching; consumers can tighten further.
    enabled: (options.enabled ?? true) && !!userId,
    // 5 minutes — this data effectively only changes from Settings.
    staleTime: options.staleTime ?? 5 * 60_000,
    gcTime: options.gcTime ?? 10 * 60_000,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
    throwOnError: options.throwOnError ?? false,
    queryFn: () => fetchDayTypes(userId!),
  });
}
